
import React, { useEffect, useState, useRef } from 'react';
import { Award } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnimatedXPProps {
  value: number;
  isLoading: boolean;
  className?: string;
}

const AnimatedXP = ({ value, isLoading, className }: AnimatedXPProps) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isIncreasing, setIsIncreasing] = useState(false);
  const prevValueRef = useRef(0);
  
  useEffect(() => {
    // Skip animation when initializing with zero or during loading
    if (isLoading || value === 0) {
      setDisplayValue(value);
      prevValueRef.current = value;
      return;
    }
    
    // Detect increase in XP to trigger animation
    if (value > prevValueRef.current) {
      setIsIncreasing(true);
      
      // Animate counting up from previous value to new value
      let start = prevValueRef.current;
      const end = value;
      const duration = 1500; // ms
      const step = Math.ceil((end - start) / (duration / 16)); // approx 60fps
      
      const animateValue = () => {
        start = Math.min(start + step, end);
        setDisplayValue(start);
        
        if (start < end) {
          requestAnimationFrame(animateValue);
        } else {
          // Animation finished
          setTimeout(() => setIsIncreasing(false), 1000);
        }
      };
      
      animateValue();
    } else {
      // If value decreased or didn't change, just set it
      setDisplayValue(value);
    }
    
    prevValueRef.current = value;
  }, [value, isLoading]);
  
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <div className={cn(
        "flex items-center gap-1 bg-muted/50 px-3 py-1 rounded-full transition-all",
        isIncreasing && "bg-yellow-100 scale-110 animate-pulse"
      )}>
        <Award className={cn(
          "h-4 w-4 transition-colors",
          isIncreasing ? "text-yellow-500" : "text-secondary"
        )} />
        <span className={cn(
          "font-medium transition-all",
          isIncreasing && "text-yellow-700"
        )}>
          {isLoading ? "Loading..." : `${displayValue} XP`}
        </span>
      </div>
      {isIncreasing && (
        <span className="text-xs font-bold text-yellow-500 animate-fade-in">
          +{value - prevValueRef.current}
        </span>
      )}
    </div>
  );
};

export default AnimatedXP;
